'use client';

import { AlertCircle, Check, Clapperboard, Film, ImageIcon, Loader2, RefreshCw, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import type { GenProposal, GenShotJob } from '@/types/editor';

interface GenerationProposalCardProps {
  proposal: GenProposal;
  onAllow: () => void;
  onDecline: () => void;
  onRetryJob?: (localId: string) => void;
}

function friendlyGenerationError(error?: string): string {
  const normalized = error?.toLowerCase() ?? '';
  if (normalized.includes('unknown model')) {
    return 'Модель временно недоступна. Повторите запуск.';
  }
  if (normalized.includes('insufficient tokens')) {
    return 'Недостаточно токенов для генерации.';
  }
  if (normalized.includes('reference upload')) {
    return 'Не удалось загрузить референс. Попробуйте ещё раз.';
  }
  if (normalized.includes('timed out')) {
    return 'Генерация заняла слишком много времени.';
  }
  if (normalized.includes('no output')) {
    return 'Сервис завершил генерацию без результата.';
  }
  return 'Не удалось запустить генерацию.';
}

/** Right-side status chip for one shot row, driven by `job.state`. */
function JobStatus({ job, onRetry }: { job: GenShotJob; onRetry?: () => void }) {
  switch (job.state) {
    case 'quoting':
      return (
        <span className="inline-flex shrink-0 items-center gap-1.5 text-[11px] font-medium text-white/40">
          <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2} />
          оценка…
        </span>
      );
    case 'awaiting':
      // Ждём решения пользователя — статус-строке нечего показывать.
      return null;
    case 'submitting':
      return (
        <span className="inline-flex shrink-0 items-center gap-1.5 text-[11px] font-medium text-white/50">
          <Loader2 className="h-3 w-3 animate-spin text-brand-300" strokeWidth={2} />
          запуск…
        </span>
      );
    case 'running':
      return (
        <span className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.04] px-2 py-1 text-[11px] font-medium text-white/50">
          <Loader2 className="h-3 w-3 animate-spin text-brand-300" strokeWidth={2} />
          генерация…
        </span>
      );
    case 'completed':
      return (
        <span className="inline-flex shrink-0 items-center gap-1.5">
          {job.src ? (
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <video
              src={`${job.src}#t=0.5`}
              muted
              playsInline
              preload="metadata"
              className="h-10 w-14 rounded object-cover"
            />
          ) : null}
          <Check className="h-3.5 w-3.5 text-emerald-400" strokeWidth={2.5} />
        </span>
      );
    case 'error':
      return (
        <div className="mt-2 rounded-lg border border-red-400/15 bg-red-400/[0.06] p-2.5">
          <div className="flex items-start gap-2 text-[11.5px] leading-snug text-red-100/80">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-300" strokeWidth={2} />
            <span className="min-w-0 flex-1">{friendlyGenerationError(job.error)}</span>
          </div>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="mt-2 inline-flex h-7 items-center gap-1.5 rounded-lg border border-red-300/20 bg-white/[0.05] px-2.5 text-[10.5px] font-semibold text-white/75 transition hover:bg-white/10"
            >
              <RefreshCw className="h-3 w-3" strokeWidth={2.2} />
              Повторить запуск
            </button>
          )}
        </div>
      );
    case 'declined':
      return <span className="shrink-0 text-[11px] font-medium text-white/30">отклонён</span>;
    default:
      return null;
  }
}

/** One shot row: number, description, kind/duration/reference badges, status. */
function ShotRow({
  job,
  displayNumber,
  onRetry,
}: {
  job: GenShotJob;
  displayNumber: number;
  onRetry?: () => void;
}) {
  const { shot } = job;
  const KindIcon = shot.kind === 'video' ? Film : ImageIcon;

  return (
    <div className="min-w-0 overflow-hidden rounded-lg border border-white/5 bg-white/[0.02] px-2.5 py-2.5">
      <div className="flex min-w-0 items-start gap-2">
        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/[0.06] text-[10px] font-bold tabular-nums text-white/50">
          {displayNumber}
        </span>
        <div className="min-w-0 flex-1">
          <p className="break-words text-[12.5px] leading-[1.45] text-white/85">{shot.description}</p>
          <div className="mt-2 flex min-w-0 flex-wrap items-center gap-1.5">
            <span className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/5 px-1.5 py-0.5 text-[10px] font-medium text-white/50">
              <KindIcon className="h-2.5 w-2.5" strokeWidth={2} />
              {shot.kind === 'video' ? 'видео' : 'фото'}
            </span>
            {shot.kind === 'video' && shot.durationSec ? (
              <span className="inline-flex items-center rounded-md border border-white/10 bg-white/5 px-1.5 py-0.5 text-[10px] font-medium text-white/50">
                {shot.durationSec}с
              </span>
            ) : null}
            {job.modelLabel && (
              <span className="inline-flex max-w-full items-center gap-1 rounded-md border border-white/10 bg-white/5 px-1.5 py-0.5 text-[10px] font-medium text-white/50">
                <Sparkles className="h-2.5 w-2.5" strokeWidth={2} />
                <span className="truncate">{job.modelLabel}</span>
              </span>
            )}
            {shot.useReferenceImage && (
              <span className="inline-flex items-center gap-1 rounded-md border border-brand-400/20 bg-brand-500/10 px-1.5 py-0.5 text-[10px] font-medium text-brand-200">
                <ImageIcon className="h-2.5 w-2.5" strokeWidth={2} />
                референс
              </span>
            )}
          </div>
        </div>
      </div>
      <JobStatus job={job} onRetry={onRetry} />
    </div>
  );
}

/**
 * In-chat Allow/Decline card for a proposed batch generation (Canny →
 * Supercomputer). Pure presentational — the store wires `onAllow`/`onDecline`
 * and updates `proposal.jobs[].state` as the poller drives each job through
 * quoting → awaiting → submitting → running → completed/error.
 */
/** Tokens = USD 1:1; show the raw value with a token unit, never a $. */
function fmtTok(n: number): string {
  return `${n.toFixed(2)} ток.`;
}

export function GenerationProposalCard({ proposal, onAllow, onDecline, onRetryJob }: GenerationProposalCardProps) {
  const jobs = [...proposal.jobs].sort((a, b) => a.shot.order - b.shot.order);
  const anyQuoting = jobs.some((j) => j.state === 'quoting');
  // A real generation always costs > 0; a zero total means the /quote calls
  // failed, so the estimate is UNKNOWN — don't let it slip past the balance
  // guard (a broke user must not Allow just because the estimate came back 0).
  const estimateUnavailable = proposal.totalEstimateUsd <= 0;
  const overBalance = estimateUnavailable
    ? proposal.balanceUsd <= 0
    : proposal.totalEstimateUsd > proposal.balanceUsd;
  const allowDisabled = anyQuoting || overBalance;

  // While the proposal is still quoting (estimate + balance not ready), show a
  // skeleton so nothing "pops in" piece by piece — reveal the full card at once.
  if (proposal.decided === 'pending' && anyQuoting) {
    return (
      <div className="mt-3 w-full rounded-xl border border-white/10 bg-[#15091f]/90 p-3 shadow-[0_20px_60px_rgba(0,0,0,0.45)] backdrop-blur-sm">
        <div className="flex items-start gap-2">
          <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/[0.06] text-brand-300">
            <Clapperboard className="h-3.5 w-3.5" strokeWidth={2} />
          </span>
          <div className="min-w-0 flex-1">
            <span className="text-[10px] font-black uppercase tracking-[0.16em] text-brand-300">
              План генерации
            </span>
            <p className="mt-0.5 break-words text-[12.5px] leading-snug text-white/70">{proposal.summary}</p>
          </div>
        </div>
        <div className="mt-3 flex flex-col gap-1.5">
          {jobs.map((job) => (
            <div key={job.localId} className="rounded-lg border border-white/5 bg-white/[0.02] p-2.5">
              <div className="h-3 w-3/4 animate-pulse rounded bg-white/10" />
              <div className="mt-2 flex gap-1.5">
                <div className="h-4 w-14 animate-pulse rounded bg-white/[0.06]" />
                <div className="h-4 w-24 animate-pulse rounded bg-white/[0.06]" />
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 flex items-center justify-between border-t border-white/10 pt-2.5">
          <div className="h-3 w-24 animate-pulse rounded bg-white/10" />
          <div className="h-3 w-28 animate-pulse rounded bg-white/[0.06]" />
        </div>
        <div className="mt-3 flex gap-2">
          <div className="h-9 flex-1 animate-pulse rounded-lg bg-white/[0.06]" />
          <div className="h-9 flex-1 animate-pulse rounded-lg bg-white/[0.04]" />
        </div>
        <p className="mt-2 text-center text-[11px] text-white/35">Готовим план…</p>
      </div>
    );
  }

  return (
    <div className="mt-3 w-full rounded-xl border border-white/10 bg-[#15091f]/90 p-3 shadow-[0_20px_60px_rgba(0,0,0,0.45)] backdrop-blur-sm">
      {/* header */}
      <div className="flex items-start gap-2">
        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/[0.06] text-brand-300">
          <Clapperboard className="h-3.5 w-3.5" strokeWidth={2} />
        </span>
        <div className="min-w-0">
          <span className="text-[10px] font-black uppercase tracking-[0.16em] text-brand-300">
            План генерации
          </span>
          <p className="mt-0.5 break-words text-[12.5px] leading-snug text-white/70">{proposal.summary}</p>
        </div>
      </div>

      {/* shot list */}
      <div className="mt-3 flex flex-col gap-1.5">
        {jobs.map((job, index) => (
          <ShotRow
            key={job.localId}
            job={job}
            displayNumber={index + 1}
            onRetry={job.state === 'error' && !job.jobId && onRetryJob
              ? () => onRetryJob(job.localId)
              : undefined}
          />
        ))}
      </div>

      {/* footer estimate */}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-white/10 pt-2.5">
        <span className="text-[11px] text-white/40">
          {proposal.totalEstimateUsd > 0 ? (
            <>
              <span className="font-bold tabular-nums text-white/70">≈ {fmtTok(proposal.totalEstimateUsd)}</span>{' '}
              (примерно)
            </>
          ) : (
            <span className="text-white/50">оценка недоступна</span>
          )}
        </span>
        <span className="text-[11px] tabular-nums text-white/40">баланс {fmtTok(proposal.balanceUsd)}</span>
      </div>

      {overBalance && proposal.decided === 'pending' && (
        <p className="mt-1.5 text-[11px] font-medium text-red-400">недостаточно средств</p>
      )}

      {/* decision area */}
      {proposal.decided === 'pending' && (
        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            onClick={onAllow}
            disabled={allowDisabled}
            className={cn(
              'inline-flex h-7 items-center gap-1 rounded-full px-3 text-[11px] font-black text-white shadow-[0_16px_34px_rgba(91,53,255,0.35)] transition',
              allowDisabled ? 'cursor-not-allowed bg-white/10 text-white/30 shadow-none' : 'bg-brand-600 hover:bg-brand-500'
            )}
          >
            Разрешить
          </button>
          <button
            type="button"
            onClick={onDecline}
            className="inline-flex h-7 items-center gap-1 rounded-full border border-white/10 bg-white/5 px-3 text-[11px] font-bold text-white/70 transition hover:border-red-400/40 hover:text-red-400"
          >
            Отклонить
          </button>
        </div>
      )}

      {proposal.decided === 'allowed' && (
        <p className="mt-3 text-[11px] font-medium text-white/50">
          {jobs.every((job) => job.state === 'completed')
            ? 'Генерация завершена'
            : jobs.some((job) => job.state === 'error')
              ? 'Некоторые сцены требуют внимания'
              : `Запущено сцен: ${jobs.length}`}
        </p>
      )}

      {proposal.decided === 'declined' && <p className="mt-3 text-[11px] font-medium text-white/30">Отклонено</p>}
    </div>
  );
}
